import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Search,
  Hotel,
  Trash2,
  Edit,
  Download,
  RefreshCw,
  RotateCcw,
  Check,
  X
} from "lucide-react";

const SettingsHoteles = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hotels, setHotels] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingHotel, setEditingHotel] = useState(null);
  const [importing, setImporting] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [newHotel, setNewHotel] = useState({
    chain_name: "",
    hotel_name: "",
    city: "",
    state: "",
    country: ""
  });

  const handleEdit = async () => {
    if (!editingHotel) return;
    try {
      await axios.put(`${API}/hotels/${editingHotel.id}`, {
        chain_name: editingHotel.chain_name,
        hotel_name: editingHotel.hotel_name,
        city: editingHotel.city,
        state: editingHotel.state,
        country: editingHotel.country
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Hotel actualizado");
      setShowEditDialog(false);
      setEditingHotel(null);
      fetchHotels();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar hotel");
    }
  };

  useEffect(() => {
    fetchHotels();
  }, [showInactive]);

  const fetchHotels = async () => {
    try {
      // Always fetch all hotels and filter in frontend based on showInactive
      const res = await axios.get(`${API}/hotels/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter based on showInactive: true = only inactive, false = only active
      const filtered = showInactive 
        ? res.data.filter(h => h.is_active === false)
        : res.data.filter(h => h.is_active !== false);
      setHotels(filtered);
    } catch (error) {
      console.error("Error fetching hotels:", error);
      toast.error("Error al cargar hoteles");
    } finally {
      setLoading(false);
    }
  };

  const handleClearAndImport = async () => {
    if (!confirm("¿Estás seguro? Esto eliminará TODOS los hoteles y los volverá a importar.")) {
      return;
    }
    setImporting(true);
    try {
      await axios.delete(`${API}/hotels/clear-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.info("Hoteles eliminados, importando...");
      
      const res = await axios.post(`${API}/hotels/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchHotels();
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.response?.data?.detail || "Error al reimportar hoteles");
    } finally {
      setImporting(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await axios.post(`${API}/hotels/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchHotels();
    } catch (error) {
      console.error("Error importing hotels:", error);
      toast.error(error.response?.data?.detail || "Error al importar hoteles");
    } finally {
      setImporting(false);
    }
  };

  const handleAdd = async () => {
    if (!newHotel.chain_name || !newHotel.hotel_name || !newHotel.city || !newHotel.country) {
      toast.error("Cadena, nombre del hotel, ciudad y país son requeridos");
      return;
    }
    try {
      await axios.post(`${API}/hotels`, newHotel, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Hotel agregado");
      setShowAddDialog(false);
      setNewHotel({ chain_name: "", hotel_name: "", city: "", state: "", country: "" });
      fetchHotels();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al agregar hotel");
    }
  };

  const handleDelete = async (hotelId) => {
    if (!confirm(`¿Inactivar este hotel?`)) return;
    try {
      await axios.delete(`${API}/hotels/${hotelId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Hotel inactivado");
      fetchHotels();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al inactivar hotel");
    }
  };

  const handleRestore = async (hotelId) => {
    try {
      await axios.post(`${API}/hotels/restore/${hotelId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Hotel restaurado");
      fetchHotels();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al restaurar hotel");
    }
  };

  const filteredHotels = hotels.filter(hotel => {
    const search = searchTerm.toLowerCase();
    return (
      hotel.chain_name?.toLowerCase().includes(search) ||
      hotel.hotel_name?.toLowerCase().includes(search) ||
      hotel.city?.toLowerCase().includes(search) ||
      hotel.state?.toLowerCase().includes(search) ||
      hotel.country?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-muted-foreground">Cargando hoteles...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="settings-hoteles-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-3xl font-bold text-slate-900">
                Hoteles
              </h1>
              <p className="text-slate-500">Catálogo de cadenas hoteleras y sus propiedades</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleImport}
              disabled={importing}
            >
              <Download className="w-4 h-4 mr-2" />
              {importing ? "Procesando..." : "Importar Nuevos"}
            </Button>
            <Button
              variant="destructive"
              onClick={handleClearAndImport}
              disabled={importing}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {importing ? "Procesando..." : "Limpiar y Reimportar"}
            </Button>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Agregar
            </Button>
          </div>
        </div>

        {/* Stats and Search */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
              <div className="flex items-center gap-4">
                <div className={`flex items-center gap-2 ${showInactive ? 'bg-red-50' : 'bg-amber-50'} px-4 py-2 rounded-lg`}>
                  <Hotel className={`w-5 h-5 ${showInactive ? 'text-red-600' : 'text-amber-600'}`} />
                  <span className={`font-semibold ${showInactive ? 'text-red-900' : 'text-amber-900'}`}>{hotels.length}</span>
                  <span className={showInactive ? 'text-red-600' : 'text-amber-600'}>
                    {showInactive ? 'hoteles inactivos' : 'hoteles activos'}
                  </span>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                    className="rounded"
                  />
                  Mostrar solo inactivos
                </label>
              </div>
              <div className="relative w-full md:w-80">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Buscar por cadena, hotel, ciudad..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="hotel-search"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Hotels Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Hotel className="w-5 h-5 text-slate-600" />
              Catálogo de Hoteles
            </CardTitle>
            <CardDescription>
              Principales cadenas hoteleras con prioridad en México, USA, Canadá y China
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border max-h-[600px] overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-white">
                  <TableRow>
                    <TableHead>Cadena</TableHead>
                    <TableHead>Hotel</TableHead>
                    <TableHead>Ciudad</TableHead>
                    <TableHead>Estado/Prov.</TableHead>
                    <TableHead>País</TableHead>
                    <TableHead className="w-[100px]">Estatus</TableHead>
                    <TableHead className="w-[100px]">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredHotels.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                        {hotels.length === 0 
                          ? "No hay hoteles. Haz clic en 'Importar Nuevos' para cargar el catálogo."
                          : "No se encontraron hoteles con ese criterio de búsqueda"
                        }
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredHotels.slice(0, 200).map((hotel) => (
                      <TableRow 
                        key={hotel.id}
                        className={hotel.is_active === false ? "opacity-50 bg-slate-50" : ""}
                      >
                        <TableCell className="font-semibold text-amber-700">
                          {hotel.chain_name}
                        </TableCell>
                        <TableCell className="font-medium">{hotel.hotel_name}</TableCell>
                        <TableCell>{hotel.city}</TableCell>
                        <TableCell className="text-slate-500">{hotel.state || "-"}</TableCell>
                        <TableCell>
                          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                            {hotel.country}
                          </span>
                        </TableCell>
                        <TableCell>
                          {hotel.is_active === false ? (
                            <span className="flex items-center gap-1 text-xs text-red-600">
                              <X className="w-3 h-3" /> Inactivo
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs text-green-600">
                              <Check className="w-3 h-3" /> Activo
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setEditingHotel({...hotel});
                                setShowEditDialog(true);
                              }}
                              className="h-8 w-8"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {hotel.is_active === false ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRestore(hotel.id)}
                                className="h-8 w-8 text-green-600 hover:text-green-700"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(hotel.id)}
                                className="h-8 w-8 text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            {filteredHotels.length > 200 && (
              <p className="text-sm text-slate-500 mt-2 text-center">
                Mostrando 200 de {filteredHotels.length} hoteles. Usa el buscador para filtrar.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Add Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Agregar Hotel</DialogTitle>
              <DialogDescription>
                Ingresa los datos del nuevo hotel
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Cadena Hotelera *</Label>
                <Input
                  value={newHotel.chain_name}
                  onChange={(e) => setNewHotel({...newHotel, chain_name: e.target.value})}
                  placeholder="Marriott"
                />
              </div>
              <div className="space-y-2">
                <Label>Nombre del Hotel *</Label>
                <Input
                  value={newHotel.hotel_name}
                  onChange={(e) => setNewHotel({...newHotel, hotel_name: e.target.value})}
                  placeholder="JW Marriott Cancún"
                />
              </div>
              <div className="space-y-2">
                <Label>Ciudad *</Label>
                <Input
                  value={newHotel.city}
                  onChange={(e) => setNewHotel({...newHotel, city: e.target.value})}
                  placeholder="Cancún"
                />
              </div>
              <div className="space-y-2">
                <Label>Estado/Provincia</Label>
                <Input
                  value={newHotel.state}
                  onChange={(e) => setNewHotel({...newHotel, state: e.target.value})}
                  placeholder="Quintana Roo"
                />
              </div>
              <div className="space-y-2">
                <Label>País *</Label>
                <Input
                  value={newHotel.country}
                  onChange={(e) => setNewHotel({...newHotel, country: e.target.value})}
                  placeholder="México"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleAdd}>
                Agregar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Editar Hotel</DialogTitle>
              <DialogDescription>
                Modifica los datos del hotel
              </DialogDescription>
            </DialogHeader>
            {editingHotel && (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Cadena Hotelera *</Label>
                  <Input
                    value={editingHotel.chain_name}
                    onChange={(e) => setEditingHotel({...editingHotel, chain_name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nombre del Hotel *</Label>
                  <Input
                    value={editingHotel.hotel_name}
                    onChange={(e) => setEditingHotel({...editingHotel, hotel_name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ciudad *</Label>
                  <Input
                    value={editingHotel.city}
                    onChange={(e) => setEditingHotel({...editingHotel, city: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Estado/Provincia</Label>
                  <Input
                    value={editingHotel.state}
                    onChange={(e) => setEditingHotel({...editingHotel, state: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>País *</Label>
                  <Input
                    value={editingHotel.country}
                    onChange={(e) => setEditingHotel({...editingHotel, country: e.target.value})}
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleEdit}>
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default SettingsHoteles;
